/**
 * Read-aloud and voice input.
 *
 * Voice input is a browser built-in and always will be in Phase 1. Read-aloud
 * has two backends behind one function (D-080):
 *
 *   1. a clip from our own voice endpoint — a real TTS voice, cached, offline
 *      once heard, with word marks from the provider
 *   2. `window.speechSynthesis` — the OS voice, which is what SPEC Section 7
 *      specified for Phase 1 and what every fallback lands on
 *
 * `speak(text, { onWord })` is the seam. It reports word boundaries either way,
 * so ReadAloud and Speaker never learn which backend spoke — for a 4- and a
 * 6-year-old the highlighting turns listening into early reading practice,
 * which is the point of the read-aloud (D-058).
 */

import { getClip } from './voice_api.js'

const synth = typeof window !== 'undefined' ? window.speechSynthesis : null
export const canSpeak = () => !!synth

/* ---------------- mute ---------------- */

const MUTE_KEY = 'ab.mute.v1'
let muted = (() => {
  try {
    return localStorage.getItem(MUTE_KEY) === '1'
  } catch {
    return false
  }
})()
const muteListeners = new Set()

export const isMuted = () => muted
export function setMuted(next) {
  muted = !!next
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0')
  } catch {
    /* ignore */
  }
  if (muted) stopSpeaking()
  muteListeners.forEach((fn) => fn(muted))
}
export function onMuteChange(fn) {
  muteListeners.add(fn)
  return () => muteListeners.delete(fn)
}

/* ---------------- speaking ---------------- */

/**
 * Whether a line is being read aloud right now, for anything that should make
 * room for the voice: the arena's sounds duck under it (D-195). Set by speak()
 * for the whole of a line, whichever backend reads it, and cleared by a stop.
 */
let speaking = false
const speakingListeners = new Set()
function setSpeaking(next) {
  if (speaking === next) return
  speaking = next
  speakingListeners.forEach((fn) => fn(next))
}
export const isSpeaking = () => speaking
export function onSpeakingChange(fn) {
  speakingListeners.add(fn)
  return () => speakingListeners.delete(fn)
}

/* ---------------- voices ---------------- */

let voice = null
function pickVoice() {
  if (voice || !synth) return voice
  const voices = synth.getVoices()
  if (!voices.length) return null
  // A warm, local, English voice. iPad ships Samantha; macOS has these too.
  voice =
    voices.find((v) => /^en/i.test(v.lang) && v.localService && /samantha|karen|moira|daniel/i.test(v.name)) ||
    voices.find((v) => /^en/i.test(v.lang) && v.localService) ||
    voices.find((v) => /^en/i.test(v.lang)) ||
    null
  return voice
}
if (synth) synth.addEventListener?.('voiceschanged', () => (voice = null))

/* ---------------- speaking ---------------- */

/** The clip currently playing, if the voice endpoint gave us one. */
let audio = null
let rafId = 0
/** Bumped by every stop, so an await that finishes late cannot start speaking. */
let generation = 0

function stopClip() {
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = 0
  }
  if (audio) {
    try {
      audio.pause()
    } catch {
      /* ignore */
    }
    audio.removeAttribute('src')
    audio = null
  }
}

export function stopSpeaking() {
  generation++
  stopClip()
  try {
    synth?.cancel()
  } catch {
    /* ignore */
  }
  setSpeaking(false)
}

/**
 * Play a clip, driving `onWord` from the provider's marks so the highlight
 * behaves exactly as it does under SpeechSynthesis.
 * @returns {Promise<boolean>} false if it could not be played at all — a dead
 *   URL, or an autoplay policy blocking audio the child did not ask for. The
 *   caller then falls back to the OS voice rather than saying nothing.
 */
function playClip(clip, onWord) {
  return new Promise((resolve) => {
    let el
    try {
      el = new Audio(clip.url)
    } catch {
      return resolve(false)
    }
    el.preload = 'auto'
    audio = el
    const marks = clip.marks || []
    let started = false
    let last = -1

    const done = (ok) => {
      if (audio === el) stopClip()
      // Once a clip has started, a later error still counts as spoken: re-reading
      // the whole line with a different voice is worse than a truncated one.
      resolve(ok || started)
    }

    const tick = () => {
      if (audio !== el) return
      const now = el.currentTime * 1000
      let k = last
      while (k + 1 < marks.length && marks[k + 1].t <= now) k++
      if (k !== last && k >= 0) {
        last = k
        onWord(marks[k].i, marks[k].len)
      }
      rafId = requestAnimationFrame(tick)
    }

    el.onended = () => done(true)
    el.onerror = () => done(false)
    el.play()
      .then(() => {
        started = true
        if (onWord && marks.length) rafId = requestAnimationFrame(tick)
      })
      .catch(() => done(false))
  })
}

/**
 * Speak text. Resolves when finished, cancelled, or immediately if muted.
 *
 * Tries the voice endpoint first — cache first, and it gives up after a beat
 * rather than leaving a child watching a silent screen — then the OS voice.
 *
 * @param {string} text
 * @param {{rate?: number, onWord?: (charIndex: number, charLength: number) => void}} opts
 *   onWord fires per word with its offset into `text`, for highlighting.
 */
export async function speak(text, { rate = 0.95, onWord } = {}) {
  if (!text || muted) return
  stopSpeaking()
  const mine = generation
  setSpeaking(true)
  try {
    // `rate` is not passed through to a clip: 0.95 was tuned to make the OS voice
    // less robotic, and slowing a real TTS voice by the same amount drags.
    // `wait: false` means an unknown voice mode resolves in the background: the
    // first line of the very first session is read with the OS voice rather than
    // leaving a child in front of a silent screen for a network round trip.
    const clip = await getClip(text, { wait: false }).catch(() => null)
    if (generation !== mine || muted) return
    if (clip && (await playClip(clip, onWord))) return
    if (generation !== mine || muted) return

    return await speakWithSynth(text, { rate, onWord })
  } finally {
    // A newer line, or a stop, owns the flag now.
    if (generation === mine) setSpeaking(false)
  }
}

function speakWithSynth(text, { rate, onWord }) {
  return new Promise((resolve) => {
    if (!synth) return resolve()
    synth.cancel()
    const u = new SpeechSynthesisUtterance(text)
    const v = pickVoice()
    if (v) u.voice = v
    u.rate = rate
    u.pitch = 1
    if (onWord) {
      u.onboundary = (e) => {
        if (e.name === 'word' || e.name === undefined) onWord(e.charIndex, e.charLength || 0)
      }
    }
    u.onend = resolve
    u.onerror = resolve
    // Safari occasionally drops an utterance queued in the same tick as a cancel.
    setTimeout(() => {
      try {
        synth.speak(u)
      } catch {
        resolve()
      }
    }, 0)
  })
}

/* ---------------- listening ---------------- */

const Recognition = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null
export const canListen = () => !!Recognition

/**
 * Listen once and resolve with the transcript. Rejects with a short reason:
 * 'unsupported' | 'denied' | 'no-speech' | 'error'.
 */
export function listenOnce({ timeoutMs = 8000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!Recognition) return reject(new Error('unsupported'))
    stopSpeaking()
    const rec = new Recognition()
    rec.lang = 'en-US'
    rec.interimResults = false
    rec.maxAlternatives = 1
    let settled = false
    const done = (fn, v) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      try {
        rec.stop()
      } catch {
        /* already stopped */
      }
      fn(v)
    }
    const timer = setTimeout(() => done(reject, new Error('no-speech')), timeoutMs)
    rec.onresult = (e) => done(resolve, e.results?.[0]?.[0]?.transcript || '')
    rec.onerror = (e) =>
      done(reject, new Error(e.error === 'not-allowed' || e.error === 'service-not-allowed' ? 'denied' : e.error === 'no-speech' ? 'no-speech' : 'error'))
    rec.onend = () => done(reject, new Error('no-speech'))
    try {
      rec.start()
    } catch {
      done(reject, new Error('error'))
    }
  })
}
